import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import '../../message.css';
import MessageHeader from '../../../../../components/layout/messageHeader';
import ModernHeader from '../../../../../components/layout/Headers/ModernHeader';
import { DEFAULT_AVATAR } from '../../../../../shared/utils/matrix/matrixRoomUtils';
import { useMatrixRoomInfo } from '../../../../../shared/utils/matrix/useMatrixRoomInfo';
import { useNavigation } from '../../../../../shared/utils/useNavigation';
import { MatrixContext } from '../../../../../MatrixProvider';
import { useMatrixRoomMessages } from '../../../../../shared/utils/matrix/useMatrixRoomMessages';
import { buildThreadItems, CenteredLoader, isNearBottom } from '../../../../../shared/utils/chatUtil';
import ChatCard from '../card/ChatCard';
import ChatActions from '../ChatActions';
import ChatComposer from '../ChatComposer';
import { useHandleTipSuccessInChat } from '../hooks/useHandleTipSuccessInChat';
import { useChatHeaderSubtitle } from '../hooks/useChatHeaderSubtitle';
import { useCreatorMessagingPurchasePolling } from '../hooks/useCreatorMessagingPurchasePolling';
import { getUserRole, isLoginWithSupportPassword, isUserAdmin } from '../../../../../shared/utils/function';
import { ROLE } from '../../../../../shared/constants/common';
import { showError } from '../../../../../shared/utils/swalHandler';
import { fetchRestrictedWords } from '../../../../../shared/actions/matrixAction';
import { WHALE_THRESHOLD } from '../../../../../shared/constants/message';
import {
  useChatMobileViewport,
  useChatPageScrollLock,
} from '../device/useChatPageViewport';

const NewChatIntroPage = () => {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigation();
  const dispatch = useDispatch();

  const { client, isInitialized, matrixUserInfo } = useContext(MatrixContext);

  const chatWrapperRef = useRef(null);
  const messagesEndRef = useRef(null);
  const openTipModalRef = useRef({});
  const callActionRef = useRef({});
  const pageRef = useRef(null);
  const actionsDisabled = isLoginWithSupportPassword() || isUserAdmin();

  const handleMissedCallBack = useCallback((kind) => {
    void callActionRef.current?.handleCallAction?.(kind);
  }, []);
  const forceScrollToBottomRef = useRef(false);

  const stateFromNav = location.state || {};
  const fallbackUsername = stateFromNav.username;
  const fallbackProfilePicture = stateFromNav.profilePicture;

  const userRole = getUserRole();
  const isUser = userRole === ROLE.USER;
  const isCreator =
    userRole === ROLE.CREATOR_SUB || userRole === ROLE.CREATOR_CONTENT;

  const {
    destUserInfo,
    loading: loadingRoomInfo,
    roomId,
    hasPriority,
    isSubscribed,
    isAlreadyMessageTip,
    messagePurchaseContentIds,
    error: roomInfoError,
    refresh: refreshRoomInfo,
  } = useMatrixRoomInfo(userId);

  useCreatorMessagingPurchasePolling({
    enabled: isCreator,
    userId,
    isRoomInfoReady: Boolean(roomId),
    refreshRoomInfo,
    client,
    roomId,
  });

  const restrictedWords = useSelector(
    (state) => state.matrix?.restrictedWords?.data || [],
  );

  useEffect(() => {
    if (!userRole) return;
    if (!isCreator) {
      showError('Access denied', 'This page is only available to creators.');
      navigate.toMessages();
    }
  }, [userRole, isCreator, navigate]);

  const myMatrixUserId = useMemo(
    () => client?.getUserId?.() || matrixUserInfo?.matrix_user_id || null,
    [client, matrixUserInfo?.matrix_user_id],
  );

  const otherDisplayName = useMemo(() => {
    if (fallbackUsername) return fallbackUsername;
    if (destUserInfo?.username) return destUserInfo.username;
    return 'Unknown';
  }, [fallbackUsername, destUserInfo]);

  const avatarSrc = useMemo(() => {
    if (fallbackProfilePicture) return fallbackProfilePicture;
    if (destUserInfo?.profile_picture) return destUserInfo.profile_picture;
    return DEFAULT_AVATAR;
  }, [fallbackProfilePicture, destUserInfo]);

  const headerSubtitle = useChatHeaderSubtitle({
    loadingRoomInfo,
    hasPriority,
    isCreator,
    destUserInfo,
  });

  const showWhaleSubscriber = useMemo(
    () => isCreator && Number(destUserInfo?.totalSixtyDays) >= WHALE_THRESHOLD,
    [isCreator, destUserInfo?.totalSixtyDays]
  );

  const {
    loading: loadingMessages,
    messages: roomMessages,
    sendLoading: isSending,
    sendTextMessage,
    sendEditMessage,
    isReady: isChatReady,
  } = useMatrixRoomMessages({
    client,
    isInitialized,
    roomId,
    myMatrixUserId,
    otherDisplayName,
  });

  const visibleMessages = useMemo(
    () => (Array.isArray(roomMessages) ? roomMessages : []),
    [roomMessages],
  );

  const threadItems = useMemo(
    () => buildThreadItems(visibleMessages),
    [visibleMessages],
  );

  const isEmptyChat = visibleMessages.length === 0;

  const isRoomInfoReady = Boolean(roomId);
  const showPreparingChat = !isRoomInfoReady;
  const showConnectingChat = isRoomInfoReady && !isChatReady;
  const showLoadingMessages = isChatReady && loadingMessages;

  const scrollToBottom = useCallback(
    (behavior = 'smooth') => {
      const container = chatWrapperRef.current;
      if (!container) return;

      container.scrollTo({
        top: container.scrollHeight,
        behavior,
      });
      if (behavior === 'auto') {
        container.scrollTop = container.scrollHeight;
      }
    },
    [],
  );

  const handleTipSuccessInChat = useHandleTipSuccessInChat({
    sendTextMessage,
    scrollToBottom,
    openTipModalRef,
    forceScrollToBottomRef,
  });

  useEffect(() => {
    if (!restrictedWords || restrictedWords.length === 0) {
      dispatch(fetchRestrictedWords());
    }
  }, [dispatch, restrictedWords]);

  // Lock and viewport for mobile devices
  useChatPageScrollLock(pageRef, chatWrapperRef);
  useChatMobileViewport({
    pageRef,
    scrollContainerRef: chatWrapperRef,
    scrollToBottom,
  });

  useEffect(() => {
    if (roomInfoError) {
      showError('Error', roomInfoError);
    }
  }, [roomInfoError]);

  useEffect(() => {
    const container = chatWrapperRef.current;
    if (!container) return;
    if (isNearBottom(container)) {
      scrollToBottom('auto');
    }
  }, [visibleMessages, scrollToBottom]);

  const handleBack = () => {
    if (stateFromNav.fromNewMessage) {
      navigate.toNewMessage();
    } else {
      navigate.toMessages();
    }
  };

  return (
    <div ref={pageRef} className="modern-message-page modern-chat-page">

      {visibleMessages.length === 0 ? (
        <ModernHeader
          customTitle={loadingRoomInfo ? 'New Message' : `To: @${otherDisplayName}`}
          showButtonBorder={false}
          hideSearchIcon
          showAvatar={false}
          showBackButton
          onBackClick={handleBack}
        />
      ) : (
        <MessageHeader
          title={loadingRoomInfo ? 'Loading...' : otherDisplayName || ''}
          profileUsername={loadingRoomInfo ? 'Loading...' : otherDisplayName || ''}
          imgSrc={avatarSrc}
          subtitle={headerSubtitle}
          showPriority={Boolean(hasPriority)}
          showWhaleSubscriber={showWhaleSubscriber}
          showBackButton
          onBackClick={handleBack}
        />
      )}

      <main className="modern-contact-main ">
        {isEmptyChat && (
          <div className="cm_new_chat_intro">
            <div className="cm_new_chat_avatar">
              <img src={avatarSrc} alt={`@${otherDisplayName}`} />
            </div>
          </div>
        )}

        <div
          id='cm_chat_wrapper'
          className={`new_chat_wrapper ${isEmptyChat ? 'new_chat_wrapper_empty' : 'new_chat_wrapper_not_empty'}${isUser ? ' cm_subscriber_chat_timestamps' : ''
            }`}
          ref={chatWrapperRef}
        >
          {showPreparingChat ? (
            <CenteredLoader size={32} text="Preparing chat..." />
          ) : showConnectingChat ? (
            <CenteredLoader text="Connecting to chat..." />
          ) : showLoadingMessages ? (
            <CenteredLoader text="Loading messages..." />
          ) : (
            <>
              <div className={`${threadItems.length === 0 ? 'for_cm_height_300' : threadItems.length < 3 ? 'for_cm_height' : ''}`}></div>

              {threadItems.map((row) =>
                row.type === 'date' ? (
                  <div
                    key={row.key}
                    className="cm_date_separator"
                    role="separator"
                    aria-label={row.label}
                  >
                    <span className="cm_date_separator_chip">{row.label}</span>
                  </div>
                ) : (
                  <ChatCard
                    key={row.key}
                    item={row.item}
                    otherAvatar={avatarSrc}
                    isUser={isUser}
                    creatorInfo={destUserInfo}
                    messagePurchaseContentIds={messagePurchaseContentIds}
                    onEditMessage={sendEditMessage}
                    roomId={roomId}
                    onCallBack={handleMissedCallBack}
                  />
                ),
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {!actionsDisabled && (
          <div
            className="chat_editer_wrapper"
            style={{
              display:
                isUser && isEmptyChat && !isAlreadyMessageTip
                  ? 'none'
                  : undefined,
            }}
          >
            <ChatActions
              userRole={userRole}
              roomId={roomId}
              otherUserId={destUserInfo?._id}
              creatorInfo={destUserInfo}
              onTipSuccessInChat={handleTipSuccessInChat}
              isEmptyChat={isEmptyChat && !isAlreadyMessageTip}
              openTipModalRef={openTipModalRef}
              callActionRef={callActionRef}
              onUploadSuccessInChat={() => { }}
              isSubscribed={isSubscribed}
              onRefreshRoomInfo={refreshRoomInfo}
            />

            <ChatComposer
              roomId={roomId}
              creatorInfo={destUserInfo}
              isOnline
              isRoomInfoReady={isRoomInfoReady}
              isChatReady={isChatReady}
              isSending={isSending}
              canSendMessages
              sendTextMessage={sendTextMessage}
              scrollToBottom={scrollToBottom}
              forceScrollToBottomRef={forceScrollToBottomRef}
              restrictedWords={restrictedWords}
              openTipModalRef={openTipModalRef}
            />
          </div>
        )}
      </main >

    </div >
  );
};

export default React.memo(NewChatIntroPage);

